import { getNextPlayerId } from '../../base/GamePlayer.js';
import { CmdResultTypes, getCard, getDeckCode, suitEqual, } from '../../shared/index.js';
import { calculateScore, initRound } from './round.js';
export function isOhHellState(state) {
    return (state &&
        state.deckCode &&
        Array.isArray(state.players) &&
        Array.isArray(state.completedRounds));
}
export function isInProgress(state) {
    return !!state?.currentRound;
}
export var OhHellCmdType;
(function (OhHellCmdType) {
    OhHellCmdType["Start"] = "START";
    OhHellCmdType["GetPlayerState"] = "GET_PLAYER_STATE";
    OhHellCmdType["DealCards"] = "DEAL";
    OhHellCmdType["PlaceBid"] = "PLACE_BID";
    OhHellCmdType["PlayCard"] = "PLAY_CARD";
    OhHellCmdType["Exit"] = "EXIT";
    OhHellCmdType["Kick"] = "KICK";
})(OhHellCmdType || (OhHellCmdType = {}));
export function isSuccess(result) {
    return result.result === CmdResultTypes.Success;
}
export function ohFail(message) {
    return { result: CmdResultTypes.Failure, message };
}
export function ohSucceed(state, events = []) {
    return { result: CmdResultTypes.Success, state, events };
}
// events - things that happened
export var OhHellEventName;
(function (OhHellEventName) {
    OhHellEventName["Start"] = "START";
    OhHellEventName["Dealt"] = "DEALT";
    OhHellEventName["BidPlaced"] = "BID_PLACED";
    OhHellEventName["BiddingComplete"] = "BIDDING_COMPLETE";
    OhHellEventName["CardPlayed"] = "CARD_PLAYED";
    OhHellEventName["TrickComplete"] = "TRICK_COMPLETE";
    OhHellEventName["RoundComplete"] = "ROUND_COMPLETE";
    OhHellEventName["EndOfGame"] = "END_OF_GAME";
})(OhHellEventName || (OhHellEventName = {}));
export var OhHellRulesMessage;
(function (OhHellRulesMessage) {
    OhHellRulesMessage["NotDealPhase"] = "Game is not in deal phase";
    OhHellRulesMessage["NotBidPhase"] = "Game is not in Bid phase";
    OhHellRulesMessage["NotPlayPhase"] = "Game is not in play phase";
    OhHellRulesMessage["NotPlayersTurn"] = "Not player's turn";
    OhHellRulesMessage["BidIsNotValid"] = "Bid is not valid";
    OhHellRulesMessage["DealerGetsScrewed"] = "Dealer gets screwed";
    OhHellRulesMessage["CardNotInHand"] = "Played card not in hand";
    OhHellRulesMessage["TrumpsNotBroken"] = "Can not lead trump suit until trump suit broken or voided of others";
    OhHellRulesMessage["MustFollowSuit"] = "Played card does not match led suit, but suited card is in hand";
    OhHellRulesMessage["CantKickLastPlayer"] = "Can not kick last player";
})(OhHellRulesMessage || (OhHellRulesMessage = {}));
export function dealtEvent(state) {
    return {
        eventName: OhHellEventName.Dealt,
        details: { dealerPlayerId: state.currentRound.dealerPlayerId },
    };
}
export function bidPlacedEvent(state) {
    const bids = state.currentRound.bids;
    return {
        eventName: OhHellEventName.BidPlaced,
        details: { bid: bids[bids.length - 1] },
    };
}
export function biddingCompleteEvent(state) {
    return {
        eventName: OhHellEventName.BiddingComplete,
        details: { bids: state.currentRound.bids },
    };
}
export function cardPlayedEvent(state) {
    if (!state.currentRound?.currentTrick?.cards?.length) {
        throw new Error('expected a card in play');
    }
    const cards = state.currentRound.currentTrick.cards;
    return {
        eventName: OhHellEventName.CardPlayed,
        details: { playerCard: cards[cards.length - 1] },
    };
}
export function trickCompleteEvent(state) {
    const tricks = state.currentRound.completedTricks;
    return {
        eventName: OhHellEventName.TrickComplete,
        details: { trick: tricks[tricks.length - 1] },
    };
}
export function roundCompleteEvent(state) {
    const score = getScore(state);
    if (!score) {
        throw new Error('expected a score here');
    }
    return {
        eventName: OhHellEventName.RoundComplete,
        details: { score },
    };
}
// module private methods
export function getTrickWinnerId(param, trick, trumpSuit) {
    const deck = getDeckCode(param);
    if (!trick.cards.length) {
        throw new Error('need to be cards in the trick');
    }
    if (trumpSuit) {
        const highestTrumpPlayed = trick.cards
            .filter((playedCard) => suitEqual(getCard(deck, playedCard.card).suit, trumpSuit))
            .reduce((maxPlayerCard, nextPlayerCard) => {
            if (!maxPlayerCard ||
                getCard(deck, maxPlayerCard.card).cardValue <
                    getCard(deck, nextPlayerCard.card).cardValue) {
                return nextPlayerCard;
            }
            return maxPlayerCard;
        }, undefined);
        if (highestTrumpPlayed) {
            return highestTrumpPlayed.playerId;
        }
    }
    const leadSuit = getCard(deck, trick.cards[0].card).suit;
    const highestSuitedPlayed = trick.cards
        .filter((playedCard) => suitEqual(getCard(deck, playedCard.card).suit, leadSuit))
        .reduce((maxPlayerCard, nextPlayerCard) => {
        if (!maxPlayerCard ||
            getCard(deck, maxPlayerCard.card).cardValue <
                getCard(deck, nextPlayerCard.card).cardValue) {
            return nextPlayerCard;
        }
        return maxPlayerCard;
    }, undefined) || trick.cards[0];
    return highestSuitedPlayed.playerId;
}
export function closeTrick(state) {
    const round = Object.assign({}, state.currentRound);
    const trickToClose = Object.assign({}, state.currentRound.currentTrick);
    const winnerId = getTrickWinnerId(state.deckCode, trickToClose, state.currentRound.trumpSuit);
    const completedTricks = [
        ...round.completedTricks,
        { cards: trickToClose.cards, winnerId },
    ];
    const updatedBids = round.bids.map((bid) => bid.playerId === winnerId ? { ...bid, wonTricks: bid.wonTricks + 1 } : bid);
    const currentRound = {
        ...round,
        completedTricks,
        turnPlayerId: winnerId,
        currentTrick: { cards: [] },
        bids: updatedBids,
    };
    return {
        ...state,
        currentRound,
    };
}
export function getScore(state) {
    const lastRound = state.completedRounds[state.completedRounds.length - 1];
    if (!lastRound) {
        return undefined;
    }
    return lastRound.bids;
}
export function closeRound(state) {
    const previousScore = getScore(state) || [];
    const score = state.currentRound.bids.map((bid) => {
        const bidScore = calculateScore(bid.bidTricks, bid.wonTricks);
        const prevScore = previousScore.find((score) => score.playerId === bid.playerId);
        return {
            ...bid,
            roundScore: bidScore,
            netScore: bidScore + (prevScore?.netScore || 0),
        };
    });
    const prevRound = {
        ...state.currentRound,
        bids: score,
        currentTrick: undefined,
    };
    const dealerPlayerId = getNextPlayerId(prevRound.dealerPlayerId, state.players);
    const completedRounds = [...state.completedRounds, prevRound];
    const currentRound = prevRound.roundNumber < state.roundsToPlay
        ? initRound(state.deckCode, state.players, prevRound.roundNumber + 1, dealerPlayerId)
        : undefined;
    return {
        ...state,
        completedRounds,
        currentRound,
    };
}
//# sourceMappingURL=state.js.map